import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { PhotonPath, SerializedMesh } from '@/workers/photon.worker';
import {
	computeClearSkySolarIrradiance,
	computePolygonAreaXY,
	PHOTON_PHYSICAL_CONFIG,
	PhotonPhysicalContext,
} from './photonPhysicalModel';
import { executePhotonWorkerPass } from './photonWorkerRunner';
import {
	clearGridCaches,
} from './photonHeatmapGrid';
import {
	initializeHeatmapRenderer,
	removePhotonHeatmapFromScene,
	setPhotonEnergyHeatmapEnabled,
	setPhotonEnergyHeatmapNormalizationMode,
	setPhotonEnergyHeatmapCellSize,
	refreshPhotonEnergyHeatmap,
	getPhotonEnergyHeatmapLegendStats,
} from './photonHeatmapRenderer';

export type {
	PhotonHeatmapCellData,
	PhotonHeatmapLegendStats,
	PhotonHeatmapNormalizationMode,
} from './photonHeatmapUtils';

export const buildSceneBVH = (scene: THREE.Scene): void => {
	scene.traverse((child) => {
		if (child instanceof THREE.Mesh && child.geometry) {
			if (!child.geometry.boundsTree) {
				child.geometry.computeBoundsTree();
			}
		}
	});
};
/**
 * Creates or updates a yellow sphere in the scene to visualize the sun position
 */
export const updateSunPositionVisualization = (scene: THREE.Scene, sunPosition: THREE.Vector3): void => {
	// Remove existing sun sphere if it exists
	const existingSun = scene.getObjectByName('SunPositionSphere');
	if (existingSun) {
		scene.remove(existingSun);
		if (existingSun instanceof THREE.Mesh) {
			existingSun.geometry?.dispose();
			if (existingSun.material instanceof THREE.Material) {
				existingSun.material.dispose();
			}
		}
	}

	// Create new sun sphere
	const sunGeometry = new THREE.SphereGeometry(5, 16, 16);
	const sunMaterial = new THREE.MeshBasicMaterial({
		color: 0xffff00, // Bright yellow
		toneMapped: false,
	});
	const sunSphere = new THREE.Mesh(sunGeometry, sunMaterial);
	sunSphere.name = 'SunPositionSphere';
	sunSphere.position.copy(sunPosition);
	sunSphere.userData.ignoreSceneClick = true;
	
	scene.add(sunSphere);
};


export const RAY_TRACING_CONFIG = {
	enabled: true,
	samples: 128,
	bounces: 15,
	maxDistance: 15000,

	environmentIntensity: 0.5,
	skyIntensity: 0.4,
	sunIntensity: 10.0,

	globalIllumination: true,
	indirectLightingStrength: 0.7,
	aoRadius: 75,
	aoIntensity: 0.6,

	adaptiveRendering: true,
	lowQualityMode: false,
} as const;

export interface LuxMeasurement {
	point: THREE.Vector3;
	lux: number;
	directLux: number;
	indirectLux: number;
	timestamp: number;
	sunElevation: number;
	isInShadow: boolean;
}

export const createDynamicEnvironmentMap = (
	renderer: THREE.WebGLRenderer,
	sunPosition: THREE.Vector3,
	_sunIntensity: number = 1.0
): THREE.Texture => {
	const pmremGenerator = new THREE.PMREMGenerator(renderer);

	const skyGeometry = new THREE.SphereGeometry(1000, 32, 16);
	const normalizedSunPos = sunPosition.clone().normalize();
	const sunElevation = Math.max(0, normalizedSunPos.z);
	const isDay = sunElevation > 0;

	const dayTopColor = new THREE.Color(0x87ceeb); // Sky blue
	const dayBottomColor = new THREE.Color(0xffffff); // White horizon
	const nightTopColor = new THREE.Color(0x001122); // Dark blue night
	const nightBottomColor = new THREE.Color(0x003344); // Darker horizon
	const sunsetColor = new THREE.Color(0xff6644); // Orange/red sunset

	// Interpolate colors based on sun elevation
	const topColor = isDay ? dayTopColor : nightTopColor;
	const bottomColor = isDay ? dayBottomColor : nightBottomColor;

	// Add sunset/sunrise tinting
	if (sunElevation < 0.3 && sunElevation > -0.1) {
		const sunsetFactor = Math.max(0, 0.3 - Math.abs(sunElevation));
		topColor.lerp(sunsetColor, sunsetFactor * 0.5);
		bottomColor.lerp(sunsetColor, sunsetFactor * 0.8);
	}

	const skyMaterial = new THREE.ShaderMaterial({
		uniforms: {
			topColor: { value: topColor },
			bottomColor: { value: bottomColor },
			sunPosition: { value: sunPosition.clone().normalize() },
			sunIntensity: { value: Math.max(0.1, sunElevation * 2) },
			offset: { value: 400 },
			exponent: { value: 0.6 },
		},
		vertexShader: `
			varying vec3 vWorldPosition;
			void main() {
				vec4 worldPosition = modelMatrix * vec4(position, 1.0);
				vWorldPosition = worldPosition.xyz;
				gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform vec3 topColor;
			uniform vec3 bottomColor;
			uniform vec3 sunPosition;
			uniform float sunIntensity;
			uniform float offset;
			uniform float exponent;
			varying vec3 vWorldPosition;
			
			void main() {
				vec3 direction = normalize(vWorldPosition);
				float h = normalize(vWorldPosition + offset).z; // Z-up coordinate system
				
				// Base sky gradient
				vec3 skyColor = mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0));
				
				// Add sun disk effect
				float sunDot = max(0.0, dot(direction, sunPosition));
				float sunDisk = smoothstep(0.996, 0.999, sunDot); // Sharp sun disk
				float sunGlow = pow(sunDot, 32.0) * 0.5; // Softer glow around sun
				
				// Combine sky with sun effects
				vec3 sunColor = vec3(1.0, 0.9, 0.7) * sunIntensity;
				skyColor += (sunDisk + sunGlow) * sunColor;
				
				gl_FragColor = vec4(skyColor, 1.0);
			}
		`,
		side: THREE.BackSide,
	});

	const skyMesh = new THREE.Mesh(skyGeometry, skyMaterial);
	const skyScene = new THREE.Scene();
	skyScene.add(skyMesh);

	const renderTarget = pmremGenerator.fromScene(skyScene);
	pmremGenerator.dispose();

	return renderTarget.texture;
};

export const createAdvancedLighting = (
	scene: THREE.Scene,
	envMap: THREE.Texture
): {
	ambientLight: THREE.AmbientLight;
	directionalLight: THREE.DirectionalLight;
} => {
	scene.environment = envMap;
	scene.environmentIntensity = RAY_TRACING_CONFIG.environmentIntensity * 1.5;

	const ambientLight = new THREE.AmbientLight(
		0x404040,
		RAY_TRACING_CONFIG.environmentIntensity * 0.5
	);
	scene.add(ambientLight);

	const directionalLight = new THREE.DirectionalLight(0xffffff, RAY_TRACING_CONFIG.sunIntensity);
	directionalLight.castShadow = true;
	directionalLight.shadow.mapSize.width = 4096;
	directionalLight.shadow.mapSize.height = 4096;
	directionalLight.shadow.camera.near = 1;
	directionalLight.shadow.camera.far = 1000;
	directionalLight.shadow.camera.left = -200;
	directionalLight.shadow.camera.right = 200;
	directionalLight.shadow.camera.top = 200;
	directionalLight.shadow.camera.bottom = -200;
	directionalLight.shadow.bias = -0.0001; // Reduce shadow acne
	directionalLight.shadow.normalBias = 0.02; // Better shadow quality

	scene.add(directionalLight);
	scene.add(directionalLight.target);

	return { ambientLight, directionalLight };
};

export const enhanceRendererForRayTracing = (renderer: THREE.WebGLRenderer): void => {
	renderer.shadowMap.enabled = true;
	renderer.shadowMap.type = THREE.PCFSoftShadowMap;
	renderer.shadowMap.autoUpdate = true;

	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.0;

	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.sortObjects = true;
};

export const updateRayTracingLighting = (
	scene: THREE.Scene,
	renderer: THREE.WebGLRenderer,
	sunPosition: THREE.Vector3,
	sunIntensity: number
): void => {
	if (!RAY_TRACING_CONFIG.enabled) return;

	const newEnvMap = createDynamicEnvironmentMap(renderer, sunPosition, sunIntensity);
	scene.environment = newEnvMap;

	scene.traverse((child) => {
		if (child instanceof THREE.HemisphereLight) {
			// Calculate proper elevation angle from sun position (Z-up coordinate system)
			const normalizedSunPos = sunPosition.clone().normalize();
			const sunElevation = Math.max(0, normalizedSunPos.z);
			const isDay = sunElevation > 0;

			if (isDay) {
				child.color.setHex(0x87ceeb); // Day sky
				child.groundColor.setHex(0x8b7355); // Day ground
				child.intensity = RAY_TRACING_CONFIG.skyIntensity * (0.5 + sunElevation);
			} else {
				child.color.setHex(0x001122); // Night sky
				child.groundColor.setHex(0x002233); // Night ground
				child.intensity = RAY_TRACING_CONFIG.skyIntensity * 0.1; // Very dim at night
			}
		}

		// Update area lights based on time of day
		if (child instanceof THREE.RectAreaLight) {
			const normalizedSunPos = sunPosition.clone().normalize();
			const sunElevation = Math.max(0, normalizedSunPos.z);
			// Interior lights are brighter when it's darker outside
			child.intensity = 8 * (1 - sunElevation + 0.2);

			// Warmer color at night, cooler during day
			const warmth = 1 - sunElevation;
			child.color.setRGB(1, 0.9 + warmth * 0.1, 0.7 + warmth * 0.2);
		}
	});
};

export const enhanceMaterialForRayTracing = (material: THREE.Material): THREE.Material => {
	if (material instanceof THREE.MeshPhysicalMaterial) {
		material.envMapIntensity = 1.0;
		material.reflectivity = Math.max(material.reflectivity, 0.1);

		if (material.transmission > 0) {
			material.thickness = Math.max(material.thickness, 0.1);
			material.attenuationDistance = Math.max(material.attenuationDistance || 1, 1);
		}

		if (material.clearcoat > 0) {
			material.clearcoatRoughness = Math.min(material.clearcoatRoughness, 0.3);
		}
	}

	return material;
};

export const updateRayTracingAfterGeometryChange = (
	scene: THREE.Scene,
	sunPosition: THREE.Vector3,
	sunIntensity: number,
	renderer?: THREE.WebGLRenderer
): { environmentUpdated: boolean } => {
	if (!RAY_TRACING_CONFIG.enabled) {
		return { environmentUpdated: false };
	}

	invalidatePhotonMap();

	let environmentUpdated = false;

	try {
		scene.children.forEach((child) => {
			if (child.type === 'Mesh') {
				const mesh = child as THREE.Mesh;
				if (mesh.geometry) {
					mesh.geometry.computeBoundingBox();
					mesh.geometry.computeBoundingSphere();
					mesh.updateMatrixWorld(true);
				}
			}
		});

		if (scene && sunPosition && renderer) {
			const newEnvMap = createDynamicEnvironmentMap(renderer, sunPosition, sunIntensity);
			scene.environment = newEnvMap;
			environmentUpdated = true;
		}
	} catch (error) {
		console.error('❌ Error updating ray tracing after geometry change:', error);
	}

	return { environmentUpdated };
};

export const triggerRayTracingUpdate = (
	scene: THREE.Scene,
	currentSunPositionRef: React.MutableRefObject<THREE.Vector3>,
	directionalLightRef: React.MutableRefObject<THREE.DirectionalLight | null>,
	rendererRef?: React.MutableRefObject<THREE.WebGLRenderer | null>
): Promise<{ environmentUpdated: boolean }> => {
	return new Promise((resolve, reject) => {
		try {
			if (!currentSunPositionRef.current || !directionalLightRef.current) {
				console.warn(
					'⚠️ Cannot update ray tracing: missing sun position or directional light'
				);
				resolve({ environmentUpdated: false });
				return;
			}

			const result = updateRayTracingAfterGeometryChange(
				scene,
				currentSunPositionRef.current,
				directionalLightRef.current.intensity,
				rendererRef?.current || undefined
			);

			resolve(result);
		} catch (error) {
			console.error('❌ Error in triggerRayTracingUpdate:', error);
			reject(error);
		}
	});
};

export interface Photon {
	position: THREE.Vector3;
	surfaceNormal?: THREE.Vector3;
	power: number; // Radiant flux carried by this photon packet (W)
	bounces: number; // Number of bounces from sun
}

export interface PackedPhotonMapData {
	positions: Float32Array;
	normals: Float32Array;
	powers: Float32Array;
	bounces: Uint16Array;
	count: number;
}

export const PHOTON_MAP_CONFIG = {
	enabled: true,
	photonCount: 500_000_000,
	maxBounces: 50,
	searchRadius: 10,
	maxPhotonsPerSearch: 1000,
	causticPhotons: 100_000_000,
	globalPhotons: 400_000_000,
	visualizePhotons: true,
	logPhotonPaths: true,
	visualizationConfig: {
		showFirstHit: true,
		showBounces: true,
		minBouncesToShow: 0,
		maxBouncesToShow: 50,
		dotSize: 0.1,
		everyNthPhoton: 1,
		visualizeFirstHits: true,
		visualizeBounces: true,
		showPaths: true,
		everyNthPath: 1, // Store all paths but show only on hover
		interactivePaths: true, // Enable interactive path visualization
		maxInteractivePaths: 2_000_000,
		sphereSegments: 4,
	},
};

/**
 * Color scheme for photon bounce visualization
 */
const PHOTON_BOUNCE_COLORS = [
	0x00ff00, // Bounce 0 (first hit) - Green
	0xffff00, // Bounce 1 - Yellow
	0xff9900, // Bounce 2 - Orange
	0xff0000, // Bounce 3 - Red
	0xff00ff, // Bounce 4 - Magenta
	0x9900ff, // Bounce 5 - Purple
	0x0099ff, // Bounce 6+ - Blue
	0x0088ff, // Bounce 7 - Blue
	0x0077ff, // Bounce 8 - Blue
	0x0066ff, // Bounce 9 - Blue
	0x0055ff, // Bounce 10 - Blue
	0x0044ff, // Bounce 11 - Blue
	0x0033ff, // Bounce 12 - Blue
	0x0022ff, // Bounce 13 - Blue
	0x0011ff, // Bounce 14 - Blue
	0x0000ff, // Bounce 15+ - Blue
	// Beyond 15 bounces, use same blue
] as const;

class PackedPhotonMap {
	private positions = new Float32Array(0);
	private normals = new Float32Array(0);
	private powers = new Float32Array(0);
	private bounces = new Uint16Array(0);
	private count = 0;

	private ensureCapacity(additionalCount: number): void {
		const required = this.count + additionalCount;
		if (required <= this.powers.length) return;

		const nextCapacity = Math.max(required, Math.max(4096, this.powers.length * 2));
		const nextPositions = new Float32Array(nextCapacity * 3);
		const nextNormals = new Float32Array(nextCapacity * 3);
		const nextPowers = new Float32Array(nextCapacity);
		const nextBounces = new Uint16Array(nextCapacity);

		nextPositions.set(this.positions.subarray(0, this.count * 3));
		nextNormals.set(this.normals.subarray(0, this.count * 3));
		nextPowers.set(this.powers.subarray(0, this.count));
		nextBounces.set(this.bounces.subarray(0, this.count));

		this.positions = nextPositions;
		this.normals = nextNormals;
		this.powers = nextPowers;
		this.bounces = nextBounces;
	}

	appendBatch(
		positions: Float32Array,
		normals: Float32Array,
		powers: Float32Array,
		bounces: Uint16Array,
		powerScale: number,
		bounceOffset: number,
		visualizer: PhotonVisualizationSystem | null
	): void {
		const incoming = powers.length;
		if (incoming === 0) return;

		this.ensureCapacity(incoming);
		const dstStart = this.count;
		const dstPosStart = dstStart * 3;

		this.positions.set(positions, dstPosStart);
		this.normals.set(normals, dstPosStart);

		const tempPosition = visualizer ? new THREE.Vector3() : null;
		for (let i = 0; i < incoming; i++) {
			const dst = dstStart + i;
			const src3 = i * 3;
			this.powers[dst] = powers[i] * powerScale;
			this.bounces[dst] = bounces[i] + bounceOffset;

			if (visualizer && tempPosition) {
				tempPosition.set(positions[src3], positions[src3 + 1], positions[src3 + 2]);
				visualizer.addPhoton(tempPosition, this.bounces[dst]);
			}
		}

		this.count += incoming;
	}

	scalePowers(scale: number): void {
		if (Math.abs(scale - 1) <= 1e-9) return;
		for (let i = 0; i < this.count; i++) {
			this.powers[i] *= scale;
		}
	}

	clear(): void {
		this.count = 0;
	}

	get length(): number {
		return this.count;
	}

	getSnapshot(): PackedPhotonMapData {
		return {
			positions: this.positions,
			normals: this.normals,
			powers: this.powers,
			bounces: this.bounces,
			count: this.count,
		};
	}
}

/**
 * Global photon map storage
 */
const globalPhotonMap = new PackedPhotonMap();
const causticPhotonMap = new PackedPhotonMap();
let photonAccumulationRuns = 0;

let photonMapValid = false;

/**
 * Interactive photon path visualization
 * Creates paths on-demand when hovering, destroys when not needed
 */
class InteractivePhotonPathVisualization {
	private pathData: Map<string, { positions: THREE.Vector3[]; colors: number[] }> = new Map();
	private activePaths: Line2[] = [];
	private lastHoveredKey: string | null = null;
	private storedPathCount = 0;

	/**
	 * Store path data (lightweight - just positions and colors)
	 */
	addPath(positions: THREE.Vector3[], bounceColors: number[]): void {
		if (positions.length < 2) return;
		if (this.storedPathCount >= PHOTON_MAP_CONFIG.visualizationConfig.maxInteractivePaths) return;
		const dotOffset = PHOTON_MAP_CONFIG.visualizationConfig.dotSize;
		const sharedPath = {
			positions: positions.map((p) => p.clone()),
			colors: [...bounceColors],
		};
		let indexed = false;

		// Store path data for every bounce position
		for (const position of positions) {
			const key = `${position.x.toFixed(2)}_${position.y.toFixed(2)}_${position.z.toFixed(2)}`;
			if (!this.pathData.has(key)) {
				this.pathData.set(key, sharedPath);
				indexed = true;
			}

			// Photon visualization points are slightly offset to avoid z-fighting.
			// Index that shifted position as well for O(1) hover lookup.
			const shiftedKey = `${position.x.toFixed(2)}_${position.y.toFixed(2)}_${(position.z + dotOffset).toFixed(2)}`;
			if (!this.pathData.has(shiftedKey)) {
				this.pathData.set(shiftedKey, sharedPath);
				indexed = true;
			}
		}

		if (indexed) {
			this.storedPathCount++;
		}
	}

	/**
	 * Creates Line2 on-demand from stored data
	 */
	private createLine2(
		positions: THREE.Vector3[],
		bounceColors: number[],
		linewidth: number
	): Line2 {
		const geometry = new LineGeometry();
		const posArray: number[] = [];
		const colArray: number[] = [];

		for (const pos of positions) {
			posArray.push(pos.x, pos.y, pos.z);
		}

		for (let i = 0; i < positions.length; i++) {
			const level = Math.min(i, bounceColors.length - 1);
			const color = new THREE.Color(bounceColors[level]);
			colArray.push(color.r, color.g, color.b);
		}

		geometry.setPositions(posArray);
		geometry.setColors(colArray);

		const material = new LineMaterial({
			vertexColors: true,
			linewidth,
			worldUnits: false,
			transparent: true,
			opacity: 0.8,
			depthWrite: false,
			depthTest: false,
		});

		material.resolution.set(window.innerWidth, window.innerHeight);

		const line = new Line2(geometry, material);
		line.userData.isPhotonPath = true;
		line.frustumCulled = false;
		line.visible = true;

		return line;
	}

	private findPathDataForPhoton(photonPosition: THREE.Vector3) {
		const key = `${photonPosition.x.toFixed(2)}_${photonPosition.y.toFixed(2)}_${photonPosition.z.toFixed(2)}`;

		function findNearestPathKey(
			pathData: Map<string, { positions: THREE.Vector3[]; colors: number[] }>,
			position: THREE.Vector3
		) {
			let minDist = Infinity;
			let nearestKey = null;
			for (const k of pathData.keys()) {
				const [x, y, z] = k.split('_').map(Number);
				const dist = Math.sqrt(
					Math.pow(position.x - x, 2) +
						Math.pow(position.y - y, 2) +
						Math.pow(position.z - z, 2)
				);
				if (dist < minDist) {
					minDist = dist;
					nearestKey = k;
				}
			}
			return nearestKey;
		}

		let pathData = this.pathData.get(key);
		if (!pathData) {
			const dotOffset = PHOTON_MAP_CONFIG.visualizationConfig.dotSize;
			const keyMinusOffset = `${photonPosition.x.toFixed(2)}_${photonPosition.y.toFixed(2)}_${(photonPosition.z - dotOffset).toFixed(2)}`;
			pathData = this.pathData.get(keyMinusOffset);
		}
		if (!pathData && this.pathData.size <= 50000) {
			const nearestKey = findNearestPathKey(this.pathData, photonPosition);
			if (nearestKey) pathData = this.pathData.get(nearestKey);
		}

		return { key, pathData };
	}

	/**
	 * Shows path for hovered photon - creates Line2 on-demand
	 */
	showPathForPhoton(
		photonPosition: THREE.Vector3,
		scene: THREE.Scene,
		camera?: THREE.Camera
	): void {
		removeAllPhotonPathsFromScene(scene);
		const { key, pathData } = this.findPathDataForPhoton(photonPosition);

		// If hovering same photon, don't recreate
		if (key === this.lastHoveredKey && this.activePaths.length > 0) {
			return;
		}

		// Remove previous path if exists
		this.hidePath(scene);

		if (!pathData) {
			console.warn('No path data found for hovered photon');
			this.lastHoveredKey = null;
			return;
		}
		let linewidth = 3;
		if (camera) {
			const distance = camera.position.distanceTo(photonPosition);
			linewidth = distance < 50 ? 3 : 2; // LOD based on distance
		}

		const line = this.createLine2(pathData.positions, pathData.colors, linewidth);
		this.activePaths = [line];
		scene.add(line);

		this.lastHoveredKey = key;
	}

	showPathsForPhotons(
		photonPositions: THREE.Vector3[],
		scene: THREE.Scene,
		hoverKey: string,
		camera?: THREE.Camera
	): void {
		removeAllPhotonPathsFromScene(scene);
		if (hoverKey === this.lastHoveredKey && this.activePaths.length > 0) {
			return;
		}

		this.hidePath(scene);

		const uniquePaths = new Map<string, { positions: THREE.Vector3[]; colors: number[] }>();
		for (const photonPosition of photonPositions) {
			const { key, pathData } = this.findPathDataForPhoton(photonPosition);
			if (!pathData) continue;
			if (!uniquePaths.has(key)) {
				uniquePaths.set(key, pathData);
			}
		}

		if (uniquePaths.size === 0) {
			this.lastHoveredKey = null;
			return;
		}

		let linewidth = 3;
		if (camera && photonPositions.length > 0) {
			const distance = camera.position.distanceTo(photonPositions[0]);
			linewidth = distance < 50 ? 3 : 2;
		}

		this.activePaths = [];
		for (const pathData of uniquePaths.values()) {
			const line = this.createLine2(pathData.positions, pathData.colors, linewidth);
			this.activePaths.push(line);
			scene.add(line);
		}

		this.lastHoveredKey = hoverKey;
	}

	hidePath(scene?: THREE.Scene): void {
		for (const activePath of this.activePaths) {
			if (scene) {
				scene.remove(activePath);
			}

			if (activePath.geometry) {
				activePath.geometry.dispose();
			}
			if (activePath.material instanceof LineMaterial) {
				activePath.material.dispose();
			}
		}

		this.activePaths = [];
		this.lastHoveredKey = null;
	}

	addToScene(_scene: THREE.Scene): void {
		// No-op
	}

	clear(scene: THREE.Scene): void {
		this.hidePath(scene);
		this.pathData.clear();
		this.storedPathCount = 0;
	}

	dispose(scene: THREE.Scene): void {
		this.clear(scene);
	}

	updateResolution(width: number, height: number): void {
		for (const activePath of this.activePaths) {
			if (activePath.material instanceof LineMaterial) {
				activePath.material.resolution.set(width, height);
			}
		}
	}
}

export const removeAllPhotonPathsFromScene = (scene: THREE.Scene): void => {
	const pathsToRemove: THREE.Object3D[] = [];

	scene.traverse((child) => {
		if (child.userData.isPhotonPath) {
			pathsToRemove.push(child);
		}
	});

	for (const path of pathsToRemove) {
		scene.remove(path);

		if (path instanceof Line2) {
			if (path.geometry) {
				path.geometry.dispose();
			}
			if (path.material instanceof LineMaterial) {
				path.material.dispose();
			}
		}
	}

	if (interactivePhotonPathVisualization) {
		interactivePhotonPathVisualization['activePaths'] = [];
		interactivePhotonPathVisualization['lastHoveredKey'] = null;
	}
};

const resetPhotonCollections = () => {
	globalPhotonMap.clear();
	causticPhotonMap.clear();
	photonAccumulationRuns = 0;
};

const scaleAccumulatedPhotonPowers = (scale: number): void => {
	globalPhotonMap.scalePowers(scale);
	causticPhotonMap.scalePowers(scale);
};

class PhotonVisualizationSystem {
	public instancedMeshes: Map<number, THREE.InstancedMesh> = new Map();
	public photonCounts: Map<number, number> = new Map();
	public maxPhotonsPerBounce: number = 50_000_000;
	private geometry: THREE.SphereGeometry;
	private dummyObject: THREE.Object3D = new THREE.Object3D();
	private capacities: Map<number, number> = new Map();
	private scene: THREE.Scene | null = null;
	private interacting = false;
	private readonly initialCapacity = 2_000;

	constructor() {
		this.geometry = new THREE.SphereGeometry(
			PHOTON_MAP_CONFIG.visualizationConfig.dotSize,
			PHOTON_MAP_CONFIG.visualizationConfig.sphereSegments,
			PHOTON_MAP_CONFIG.visualizationConfig.sphereSegments
		);
	}

	initialize(scene: THREE.Scene, preserveExisting: boolean = false): void {
		this.scene = scene;

		if (!preserveExisting) {
			for (const [bounceLevel, instancedMesh] of this.instancedMeshes) {
				this.photonCounts.set(bounceLevel, 0);
				instancedMesh.count = 0;
				instancedMesh.visible = false;
				instancedMesh.instanceMatrix.needsUpdate = true;
			}
		}
		// World matrices are kept current by the animation loop — no need for a full traversal here
	}

	private ensureMesh(bounceLevel: number): THREE.InstancedMesh | null {
		let instancedMesh = this.instancedMeshes.get(bounceLevel);
		if (instancedMesh || !this.scene) {
			return instancedMesh ?? null;
		}

		const capacity = this.initialCapacity;
		instancedMesh = this.createInstancedMesh(bounceLevel, capacity);

		this.scene.add(instancedMesh);
		instancedMesh.updateMatrixWorld(true);

		this.instancedMeshes.set(bounceLevel, instancedMesh);
		this.photonCounts.set(bounceLevel, 0);
		this.capacities.set(bounceLevel, capacity);

		return instancedMesh;
	}

	private createInstancedMesh(bounceLevel: number, capacity: number): THREE.InstancedMesh {
		let level = bounceLevel;
		if (level >= PHOTON_BOUNCE_COLORS.length) {
			level = PHOTON_BOUNCE_COLORS.length - 1;
		}

		const material = new THREE.MeshBasicMaterial({
			color: PHOTON_BOUNCE_COLORS[level],
			transparent: true,
			opacity: 0.7,
			depthWrite: false,
		});

		const instancedMesh = new THREE.InstancedMesh(this.geometry, material, capacity);
		instancedMesh.userData.isPhotonVisualization = true;
		instancedMesh.userData.bounceLevel = bounceLevel;
		instancedMesh.castShadow = false;
		instancedMesh.receiveShadow = false;
		instancedMesh.frustumCulled = false;
		instancedMesh.count = 0;
		instancedMesh.visible = false;
		instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

		return instancedMesh;
	}

	private growIfNeeded(
		bounceLevel: number,
		requiredInstances: number
	): THREE.InstancedMesh | null {
		const currentMesh = this.ensureMesh(bounceLevel);
		if (!currentMesh) return null;

		const currentCapacity = this.capacities.get(bounceLevel) ?? this.initialCapacity;
		if (requiredInstances < currentCapacity) {
			return currentMesh;
		}

		const newCapacity = Math.min(
			this.maxPhotonsPerBounce,
			Math.max(requiredInstances, currentCapacity * 2)
		);

		if (newCapacity === currentCapacity) {
			return currentMesh;
		}

		const newMesh = this.createInstancedMesh(bounceLevel, newCapacity);
		const currentCount = this.photonCounts.get(bounceLevel) ?? 0;

		for (let i = 0; i < currentCount; i++) {
			currentMesh.getMatrixAt(i, this.dummyObject.matrix);
			newMesh.setMatrixAt(i, this.dummyObject.matrix);
		}

		newMesh.count = currentMesh.count;
		newMesh.visible = currentMesh.visible;
		newMesh.instanceMatrix.needsUpdate = true;

		if (this.scene) {
			this.scene.add(newMesh);
			currentMesh.parent?.remove(currentMesh);
		}

		this.instancedMeshes.set(bounceLevel, newMesh);
		this.capacities.set(bounceLevel, newCapacity);

		currentMesh.dispose();
		if (currentMesh.material instanceof THREE.Material) {
			currentMesh.material.dispose();
		}

		return newMesh;
	}

	private shrinkIfWarranted(bounceLevel: number): void {
		const mesh = this.instancedMeshes.get(bounceLevel);
		if (!mesh || !this.scene) return;

		const currentCount = this.photonCounts.get(bounceLevel) ?? 0;
		const currentCapacity = this.capacities.get(bounceLevel) ?? this.initialCapacity;

		if (currentCapacity <= this.initialCapacity) return;
		if (currentCount >= currentCapacity * 0.35) return;

		const targetCapacity = Math.max(
			this.initialCapacity,
			Math.min(this.maxPhotonsPerBounce, Math.ceil(currentCount * 1.25))
		);

		if (targetCapacity >= currentCapacity) return;

		const newMesh = this.createInstancedMesh(bounceLevel, targetCapacity);

		for (let i = 0; i < currentCount; i++) {
			mesh.getMatrixAt(i, this.dummyObject.matrix);
			newMesh.setMatrixAt(i, this.dummyObject.matrix);
		}

		newMesh.count = currentCount;
		newMesh.visible = mesh.visible;
		newMesh.instanceMatrix.needsUpdate = true;

		this.scene.add(newMesh);
		mesh.parent?.remove(mesh);

		this.instancedMeshes.set(bounceLevel, newMesh);
		this.capacities.set(bounceLevel, targetCapacity);

		mesh.dispose();
		if (mesh.material instanceof THREE.Material) {
			mesh.material.dispose();
		}
	}

	addPhoton(
		position: THREE.Vector3,
		bounceNumber: number,
		surfaceNormal?: THREE.Vector3
	): boolean {
		const bounceLevel = Math.min(bounceNumber, PHOTON_BOUNCE_COLORS.length - 1);
		const currentCount = this.photonCounts.get(bounceLevel) ?? 0;
		const nextCount = currentCount + 1;

		const instancedMesh = this.growIfNeeded(bounceLevel, nextCount);
		if (!instancedMesh) {
			return false;
		}

		if (nextCount > this.maxPhotonsPerBounce) {
			return false;
		}

		const offsetPosition = position.clone();
		if (surfaceNormal) {
			offsetPosition.add(
				surfaceNormal
					.clone()
					.normalize()
					.multiplyScalar(PHOTON_MAP_CONFIG.visualizationConfig.dotSize)
			);
		} else {
			offsetPosition.z += PHOTON_MAP_CONFIG.visualizationConfig.dotSize;
		}

		this.dummyObject.position.copy(offsetPosition);
		this.dummyObject.scale.set(1, 1, 1);
		this.dummyObject.updateMatrix();
		instancedMesh.setMatrixAt(currentCount, this.dummyObject.matrix);

		this.photonCounts.set(bounceLevel, nextCount);
		instancedMesh.count = nextCount;
		instancedMesh.visible = true;
		instancedMesh.instanceMatrix.needsUpdate = true;

		return true;
	}

	finalize(): void {
		// Remove bounce-level meshes with count=0 to reduce draw calls
		const toRemove: number[] = [];
		for (const [bounceLevel, instancedMesh] of this.instancedMeshes) {
			const count = this.photonCounts.get(bounceLevel) ?? 0;
			instancedMesh.count = count;
			instancedMesh.visible = count > 0 && this.shouldShowBounce(bounceLevel);
			instancedMesh.instanceMatrix.needsUpdate = true;
			instancedMesh.updateMatrix();
			instancedMesh.updateMatrixWorld(true);

			if (count > 0) {
				instancedMesh.computeBoundingSphere();
				instancedMesh.geometry.computeBoundingSphere();
				instancedMesh.geometry.computeBoundingBox();
			} else {
				// Mark empty bounce meshes for removal
				toRemove.push(bounceLevel);
			}

			this.shrinkIfWarranted(bounceLevel);
		}

		// Remove empty bounce-level meshes from scene
		for (const bounceLevel of toRemove) {
			const mesh = this.instancedMeshes.get(bounceLevel);
			if (mesh && this.scene) {
				this.scene.remove(mesh);
				this.instancedMeshes.delete(bounceLevel);
				mesh.dispose();
				if (mesh.material instanceof THREE.Material) {
					mesh.material.dispose();
				}
			}
		}

		for (const instancedMesh of this.instancedMeshes.values()) {
			if (instancedMesh.material) {
				(instancedMesh.material as THREE.Material).needsUpdate = true;
			}
		}

		this.applyRenderBudget();
	}

	setInteractionMode(interacting: boolean): void {
		if (this.interacting === interacting) return;
		this.interacting = interacting;
		this.applyRenderBudget();
	}

	refreshVisibility(): void {
		this.applyRenderBudget();
	}

	private applyRenderBudget(): void {
		if (!photonVisualizationsEnabled) {
			for (const [, mesh] of this.instancedMeshes) {
				mesh.visible = false;
				mesh.count = 0;
			}
			return;
		}

		for (const [bounceLevel, mesh] of this.instancedMeshes) {
			const fullCount = this.photonCounts.get(bounceLevel) ?? 0;
			if (fullCount <= 0 || !this.shouldShowBounce(bounceLevel)) {
				mesh.visible = false;
				mesh.count = 0;
				continue;
			}

			// During camera interaction, keep all bounced photons visible but hide first-hit photons.
			const hideFirstHitDuringInteraction = this.interacting && bounceLevel === 0;
			mesh.count = hideFirstHitDuringInteraction ? 0 : fullCount;
			mesh.visible = mesh.count > 0;
		}
	}

	private shouldShowBounce(bounceLevel: number): boolean {
		return shouldShowPhotonBounce(bounceLevel);
	}

	clear(
		scene: THREE.Scene,
		options: { detach?: boolean; disposeResources?: boolean } = {}
	): void {
		const { detach = false, disposeResources = false } = options;

		for (const [bounceLevel, instancedMesh] of this.instancedMeshes) {
			if (detach) {
				instancedMesh.parent?.remove(instancedMesh);
			}

			this.photonCounts.set(bounceLevel, 0);
			instancedMesh.count = 0;
			instancedMesh.visible = false;
			instancedMesh.instanceMatrix.needsUpdate = true;

			if (disposeResources) {
				instancedMesh.dispose();
				if (instancedMesh.material instanceof THREE.Material) {
					instancedMesh.material.dispose();
				}
			}
		}

		if (disposeResources) {
			this.instancedMeshes.clear();
			this.photonCounts.clear();
			this.capacities.clear();
			this.scene = null;
		}
	}
}

let photonVisualizationSystem: PhotonVisualizationSystem | null = null;
let interactivePhotonPathVisualization: InteractivePhotonPathVisualization | null = null;
let photonVisualizationsEnabled = true;

const shouldShowPhotonBounce = (bounceLevel: number): boolean => {
	if (bounceLevel < PHOTON_MAP_CONFIG.visualizationConfig.minBouncesToShow) return false;
	if (bounceLevel > PHOTON_MAP_CONFIG.visualizationConfig.maxBouncesToShow) return false;
	if (bounceLevel === 0 && !PHOTON_MAP_CONFIG.visualizationConfig.visualizeFirstHits)
		return false;
	if (bounceLevel > 0 && !PHOTON_MAP_CONFIG.visualizationConfig.visualizeBounces)
		return false;
	return true;
};

export const updatePhotonMapAfterGeometryChange = (scene: THREE.Scene): void => {
	invalidatePhotonMap();

	scene.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			if (child.geometry) {
				child.geometry.computeBoundingBox();
				child.geometry.computeBoundingSphere();

				if (child.geometry.boundsTree) {
					child.geometry.disposeBoundsTree();
					child.geometry.computeBoundsTree();
				}
			}

			child.updateMatrix();
			child.updateMatrixWorld(true);
		}
	});

	buildSceneBVH(scene);

	scene.updateMatrixWorld(true);
};

export const showPhotonPathOnHover = (
	photonPosition: THREE.Vector3,
	scene: THREE.Scene,
	camera?: THREE.Camera // ✅ Make camera optional
): void => {
	if (interactivePhotonPathVisualization) {
		interactivePhotonPathVisualization.showPathForPhoton(photonPosition, scene, camera);
	}
};

export const showPhotonPathsForHeatmapCell = (
	photonPositions: THREE.Vector3[],
	cellKey: string,
	scene: THREE.Scene,
	camera?: THREE.Camera
): void => {
	if (interactivePhotonPathVisualization) {
		interactivePhotonPathVisualization.showPathsForPhotons(
			photonPositions,
			scene,
			cellKey,
			camera
		);
	}
};

export const invalidatePhotonMap = (): void => {
	photonMapValid = false;
};

export const hidePhotonVisualizations = (scene: THREE.Scene): void => {
	photonVisualizationsEnabled = false;

	if (!photonVisualizationSystem) {
		return;
	}

	for (const [, instancedMesh] of photonVisualizationSystem.instancedMeshes) {
		instancedMesh.visible = false;
	}

	if (interactivePhotonPathVisualization) {
		interactivePhotonPathVisualization.hidePath();

		scene.traverse((child) => {
			if (child.userData.isPhotonPath) {
				child.visible = false;
			}
		});
	}
};

export const showPhotonVisualizations = (): void => {
	photonVisualizationsEnabled = true;

	if (!photonVisualizationSystem) {
		return;
	}

	photonVisualizationSystem.refreshVisibility();
};

export const setPhotonVisualizationInteractionMode = (interacting: boolean): void => {
	if (!photonVisualizationSystem) {
		return;
	}

	photonVisualizationSystem.setInteractionMode(interacting);
};

export const setPhotonVisualizationBounceRange = (
	minBounce: number,
	maxBounce: number
): void => {
	const clampedMin = Math.max(0, Math.floor(minBounce));
	const clampedMax = Math.max(clampedMin, Math.floor(maxBounce));

	PHOTON_MAP_CONFIG.visualizationConfig.minBouncesToShow = clampedMin;
	PHOTON_MAP_CONFIG.visualizationConfig.maxBouncesToShow = clampedMax;

	photonVisualizationSystem?.refreshVisibility();
};

export const arePhotonVisualizationsActive = (): boolean => {
	return photonVisualizationSystem !== null && photonMapValid;
};

export const GRID_PHOTON_CONFIG = {
	enabled: true,
	gridCellSize: 20.0,
	photonsPerCell: 10_000,
	visualizeGrid: true,
	parallelChunkSize: navigator.hardwareConcurrency ?? 4,
	maxTotalPhotons: 5_000_000,
	sceneBoundsPadding: 50.0,
	distanceToSiteLimit: 20.0,
} as const;

interface PhotonGridCell {
	index: { x: number; y: number };
	bounds: {
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
		minZ: number;
		maxZ: number;
	};
	photonCount: number;
	isProcessed: boolean;
}

class GridVisualization {
	private gridLines: THREE.LineSegments[] = [];
	private processedCells: THREE.Mesh[] = [];
	private activeCellHighlights: Map<string, THREE.Mesh> = new Map();

	visualizeGrid(cells: PhotonGridCell[], scene: THREE.Scene, zHeight: number = 0.1): void {
		this.clear(scene);

		// Draw grid lines
		const positions: number[] = [];

		cells.forEach((cell) => {
			const { minX, maxX, minY, maxY } = cell.bounds;

			// Draw rectangle for this cell
			positions.push(minX, minY, zHeight, maxX, minY, zHeight);
			positions.push(maxX, minY, zHeight, maxX, maxY, zHeight);
			positions.push(maxX, maxY, zHeight, minX, maxY, zHeight);
			positions.push(minX, maxY, zHeight, minX, minY, zHeight);
		});

		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

		const material = new THREE.LineBasicMaterial({
			color: 0x00ffff, // Cyan grid
			transparent: false,
			opacity: 1.0,
			depthTest: false,
			depthWrite: false,
		});

		const gridLines = new THREE.LineSegments(geometry, material);
		gridLines.userData.isGridVisualization = true;
		gridLines.renderOrder = 1000;

		scene.add(gridLines);
		this.gridLines.push(gridLines);
	}

	/**
	 * Clears all grid visualizations (lines and highlights)
	 */
	clear(scene: THREE.Scene): void {
		// Clear grid lines
		this.gridLines.forEach((line) => {
			scene.remove(line);
			line.geometry.dispose();
			if (line.material instanceof THREE.Material) {
				line.material.dispose();
			}
		});

		// Clear legacy processed cells
		this.processedCells.forEach((mesh) => {
			scene.remove(mesh);
			mesh.geometry.dispose();
			if (mesh.material instanceof THREE.Material) {
				mesh.material.dispose();
			}
		});

		this.gridLines = [];
		this.processedCells = [];
	}
}

export function isPointInPolygonXY(point: THREE.Vector3, polygon: THREE.Vector3[]): boolean {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].x,
			yi = polygon[i].y;
		const xj = polygon[j].x,
			yj = polygon[j].y;
		const px = point.x,
			py = point.y;
		const intersect =
			yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-12) + xi;
		if (intersect) inside = !inside;
	}
	return inside;
}

const isPointInCellXY = (
	point: THREE.Vector3,
	minX: number,
	maxX: number,
	minY: number,
	maxY: number
): boolean => point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;

const orientation2D = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): number => {
	return (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
};

const onSegment2D = (a: THREE.Vector3, b: THREE.Vector3, p: THREE.Vector3): boolean => {
	const eps = 1e-8;
	return (
		Math.min(a.x, b.x) - eps <= p.x &&
		p.x <= Math.max(a.x, b.x) + eps &&
		Math.min(a.y, b.y) - eps <= p.y &&
		p.y <= Math.max(a.y, b.y) + eps
	);
};

const segmentsIntersect2D = (
	a1: THREE.Vector3,
	a2: THREE.Vector3,
	b1: THREE.Vector3,
	b2: THREE.Vector3
): boolean => {
	const o1 = orientation2D(a1, a2, b1);
	const o2 = orientation2D(a1, a2, b2);
	const o3 = orientation2D(b1, b2, a1);
	const o4 = orientation2D(b1, b2, a2);

	if (o1 * o2 < 0 && o3 * o4 < 0) return true;

	const eps = 1e-8;
	if (Math.abs(o1) <= eps && onSegment2D(a1, a2, b1)) return true;
	if (Math.abs(o2) <= eps && onSegment2D(a1, a2, b2)) return true;
	if (Math.abs(o3) <= eps && onSegment2D(b1, b2, a1)) return true;
	if (Math.abs(o4) <= eps && onSegment2D(b1, b2, a2)) return true;

	return false;
};

const doesCellIntersectPolygonXY = (
	minX: number,
	maxX: number,
	minY: number,
	maxY: number,
	polygon: THREE.Vector3[]
): boolean => {
	if (polygon.length < 3) return true;

	const c0 = new THREE.Vector3(minX, minY, 0);
	const c1 = new THREE.Vector3(maxX, minY, 0);
	const c2 = new THREE.Vector3(maxX, maxY, 0);
	const c3 = new THREE.Vector3(minX, maxY, 0);
	const cellCorners = [c0, c1, c2, c3];

	for (const corner of cellCorners) {
		if (isPointInPolygonXY(corner, polygon)) return true;
	}

	for (const vertex of polygon) {
		if (isPointInCellXY(vertex, minX, maxX, minY, maxY)) return true;
	}

	const cellEdges: Array<[THREE.Vector3, THREE.Vector3]> = [
		[c0, c1],
		[c1, c2],
		[c2, c3],
		[c3, c0],
	];

	for (let i = 0; i < polygon.length; i++) {
		const a = polygon[i];
		const b = polygon[(i + 1) % polygon.length];

		for (const [e0, e1] of cellEdges) {
			if (segmentsIntersect2D(a, b, e0, e1)) {
				return true;
			}
		}
	}

	return false;
};

let gridVisualization: GridVisualization | null = null;

let lastPhotonGridCells: PhotonGridCell[] = [];

// Initialize the heatmap renderer with dependency injection
initializeHeatmapRenderer(
	(bounces: number) => shouldShowPhotonBounce(bounces),
	() => ({
		global: globalPhotonMap.getSnapshot(),
		caustic: causticPhotonMap.getSnapshot(),
	})
);

// Re-export functions from photonHeatmapRenderer for backward compatibility
export {
	setPhotonEnergyHeatmapEnabled,
	setPhotonEnergyHeatmapNormalizationMode,
	setPhotonEnergyHeatmapCellSize,
	refreshPhotonEnergyHeatmap,
	getPhotonEnergyHeatmapLegendStats,
};

const divideSceneIntoGrid = (
	scene: THREE.Scene,
	cellSize: number,
	siteLimit: THREE.Vector3[] // Array of points defining site boundary
): PhotonGridCell[] => {
	// Calculate scene bounds (Z-up coordinate system)
	const sceneBox = new THREE.Box3();
	scene.traverse((child) => {
		if (
			child instanceof THREE.Mesh &&
			child.userData.isSolid === true
		) {
			sceneBox.expandByObject(child);
		}
	});

	sceneBox.expandByScalar(GRID_PHOTON_CONFIG.sceneBoundsPadding);
	const sceneMin = sceneBox.min;
	const sceneMax = sceneBox.max;
	const hasSiteLimitPolygon = siteLimit.length >= 3;

	if (hasSiteLimitPolygon) {
		for (const point of siteLimit) {
			sceneMin.x = Math.min(sceneMin.x, point.x);
			sceneMin.y = Math.min(sceneMin.y, point.y);
			sceneMax.x = Math.max(sceneMax.x, point.x);
			sceneMax.y = Math.max(sceneMax.y, point.y);
		}
	}

	const gridWidth = sceneMax.x - sceneMin.x;
	const gridHeight = sceneMax.y - sceneMin.y;

	const numCellsX = Math.ceil(gridWidth / cellSize);
	const numCellsY = Math.ceil(gridHeight / cellSize);

	const cells: PhotonGridCell[] = [];

	const offsetX = 0;
	const offsetY = 0;

	for (let y = 0; y < numCellsY; y++) {
		for (let x = 0; x < numCellsX; x++) {
			const minX = sceneMin.x + x * cellSize + offsetX;
			const maxX = Math.min(sceneMin.x + (x + 1) * cellSize + offsetX, sceneMax.x);
			const minY = sceneMin.y + y * cellSize + offsetY;
			const maxY = Math.min(sceneMin.y + (y + 1) * cellSize + offsetY, sceneMax.y);

			if (hasSiteLimitPolygon) {
				if (!doesCellIntersectPolygonXY(minX, maxX, minY, maxY, siteLimit)) {
					continue;
				}
			}

			cells.push({
				index: { x, y },
				bounds: {
					minX,
					maxX,
					minY,
					maxY,
					minZ: sceneMin.z,
					maxZ: sceneMax.z,
				},
				photonCount: 0,
				isProcessed: false,
			});
		}
	}

	return cells;
};

export const clearAllPhotons = (scene: THREE.Scene): void => {
	if (photonVisualizationSystem) {
		photonVisualizationSystem.clear(scene);
	}

	removePhotonHeatmapFromScene(scene);

	invalidatePhotonMap();
	resetPhotonCollections();

	if (interactivePhotonPathVisualization) {
		interactivePhotonPathVisualization.dispose(scene);
		interactivePhotonPathVisualization = null;
	}

	lastPhotonGridCells.length = 0;
};

export const setPhotonEnergyHeatmapFacadeBuildingPath = (_buildingPath: string | null): void => {
	// Clear grid caches when facade building selection changes
	clearGridCaches();
};

export const getPhotonAccumulationStats = (): {
	totalPhotons: number;
	globalPhotons: number;
	causticPhotons: number;
	capacityUsed: number;
	canAddMore: boolean;
} => {
	const totalPhotons = globalPhotonMap.length + causticPhotonMap.length;
	const maxPhotons = GRID_PHOTON_CONFIG.maxTotalPhotons;

	return {
		totalPhotons,
		globalPhotons: globalPhotonMap.length,
		causticPhotons: causticPhotonMap.length,
		capacityUsed: (totalPhotons / maxPhotons) * 100,
		canAddMore: totalPhotons < maxPhotons,
	};
};

export function isPointInPolygon2D(point: THREE.Vector2, polygon: THREE.Vector2[]): boolean {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].x;
		const yi = polygon[i].y;
		const xj = polygon[j].x;
		const yj = polygon[j].y;
		const px = point.x;
		const py = point.y;

		const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
		if (intersect) inside = !inside;
	}
	return inside;
}

export const buildPhotonMapWithGridParallel = async (
	sunPosition: THREE.Vector3,
	scene: THREE.Scene,
	progressCallback?: (progress: number, cellInfo?: { current: number; total: number }) => void,
	options: { appendPaths?: boolean; physicalContext?: PhotonPhysicalContext } = {}
): Promise<void> => {
	const { appendPaths = false, physicalContext } = options;
	const currentPhotonCount = globalPhotonMap.length + causticPhotonMap.length;
	const previousRuns = currentPhotonCount > 0 ? Math.max(1, photonAccumulationRuns) : 0;
	const nextRuns = previousRuns + 1;
	const existingPhotonScale = previousRuns > 0 ? previousRuns / nextRuns : 1;
	const newPhotonScale = 1 / nextRuns;

	// Progressive accumulation: keep total energy stable by averaging runs.
	if (previousRuns > 0) {
		scaleAccumulatedPhotonPowers(existingPhotonScale);
	}

	if (currentPhotonCount >= GRID_PHOTON_CONFIG.maxTotalPhotons) {
		console.warn(
			`Maximum photon limit reached (${currentPhotonCount}/${GRID_PHOTON_CONFIG.maxTotalPhotons}). Clear photons to continue.`
		);
		return;
	}

	if (!photonVisualizationSystem) {
		photonVisualizationSystem = new PhotonVisualizationSystem();
		photonVisualizationSystem.initialize(scene, false);
	}
	photonVisualizationSystem.initialize(scene, true);

	if (
		PHOTON_MAP_CONFIG.visualizationConfig.showPaths &&
		PHOTON_MAP_CONFIG.visualizationConfig.interactivePaths
	) {
		if (!interactivePhotonPathVisualization) {
			interactivePhotonPathVisualization = new InteractivePhotonPathVisualization();
		}
		const shouldPreserveExistingPaths = appendPaths || currentPhotonCount > 0;
		if (!shouldPreserveExistingPaths) {
			interactivePhotonPathVisualization.clear(scene);
		}
	}
	invalidatePhotonMap();

	const siteLimit = scene.getObjectByName('Site Limit');
	const siteLimitPoints: THREE.Vector3[] = [];
	if (siteLimit && siteLimit instanceof THREE.Line) {
		const positionAttr = siteLimit.geometry.getAttribute('position');
		for (let i = 0; i < positionAttr.count; i++) {
			const vertex = new THREE.Vector3().fromBufferAttribute(positionAttr, i);
			// Apply world transformation to get correct coordinates
			vertex.applyMatrix4(siteLimit.matrixWorld);
			siteLimitPoints.push(vertex);
		}
	}
	lastPhotonGridCells = divideSceneIntoGrid(
		scene,
		GRID_PHOTON_CONFIG.gridCellSize,
		siteLimitPoints
	);

	const sceneBox = new THREE.Box3();
	scene.traverse((child) => {
		if (
			child instanceof THREE.Mesh &&
			child.userData.isSolid === true
		) {
			sceneBox.expandByObject(child);
		}
	});
	sceneBox.expandByScalar(10.0);

	const sceneCenter = new THREE.Vector3();
	sceneBox.getCenter(sceneCenter);

	const sceneSize = new THREE.Vector3();
	sceneBox.getSize(sceneSize);

	const maxSceneExtent = Math.max(sceneSize.x, sceneSize.y);
	const sceneDiskRadius = maxSceneExtent * 1.2;

	const shadowCasters: SerializedMesh[] = [];

	scene.traverse((child) => {
		if (child instanceof THREE.Mesh && child.userData.isSolid && child.geometry) {
			const geometry = child.geometry;
			const positionAttr = geometry.attributes.position;
			if (!positionAttr) return;

			// Use TypedArrays directly — structured clone of Float32Array is ~100x faster than number[]
			const vertices = new Float32Array(positionAttr.array);
			const indices = geometry.index
				? new Uint32Array(geometry.index.array)
				: new Uint32Array(0);
			const matrix = child.matrixWorld.toArray();
			// Tag with the scene category so the worker can look up physical properties
			const materialType: string = child.userData.category ?? 'default';

			shadowCasters.push({ vertices, indices, matrix, isSolid: true, materialType });
		}

		// Painted feature areas (water, greenery, park) are not flagged isSolid but
		// should participate in photon scattering with their own material properties.
		if (child instanceof THREE.Mesh && child.userData.isPaintedArea && child.geometry) {
			const featureType: string = child.userData.featureType ?? 'default';
			const geometry = child.geometry;
			const positionAttr = geometry.attributes.position;
			if (!positionAttr) return;

			const vertices = new Float32Array(positionAttr.array);
			const indices = geometry.index
				? new Uint32Array(geometry.index.array)
				: new Uint32Array(0);
			const matrix = child.matrixWorld.toArray();

			shadowCasters.push({ vertices, indices, matrix, isSolid: true, materialType: featureType });
		}
	});

	// Yield to the browser so the UI remains responsive before spawning workers
	await new Promise<void>((resolve) => setTimeout(resolve, 0));

	const availableBudget = Math.max(
		0,
		GRID_PHOTON_CONFIG.maxTotalPhotons - currentPhotonCount
	);
	if (availableBudget === 0) {
		console.warn('Photon budget exhausted. Clear fewer photons or raise maxTotalPhotons.');
		return;
	}

	const requestedBudget = lastPhotonGridCells.length * GRID_PHOTON_CONFIG.photonsPerCell;
	let photonsPerCell: number = GRID_PHOTON_CONFIG.photonsPerCell;

	if (requestedBudget > availableBudget) {
		photonsPerCell = Math.max(
			1,
			Math.floor(availableBudget / Math.max(1, lastPhotonGridCells.length))
		);
		console.warn(
			`Reducing photonsPerCell to ${photonsPerCell} to stay within the ${GRID_PHOTON_CONFIG.maxTotalPhotons} photon cap.`
		);
	}

	if (photonsPerCell <= 0) {
		console.warn('No photon budget left for this rebuild.');
		return;
	}

	const solarIrradiance = computeClearSkySolarIrradiance(sunPosition, physicalContext);
	if (solarIrradiance.globalHorizontalIrradianceWm2 <= 0) {
		console.warn('Sun is below horizon; skipping physically-calibrated photon emission.');
		return;
	}

	const gridHeight = sceneBox.min.z + 1.0;

	if (GRID_PHOTON_CONFIG.visualizeGrid) {
		gridVisualization = new GridVisualization();
		gridVisualization.visualizeGrid(lastPhotonGridCells, scene, gridHeight);
	}

	const siteAreaM2 = computePolygonAreaXY(siteLimitPoints);
	const gridAreaM2 = lastPhotonGridCells.reduce((sum, cell) => {
		const area = (cell.bounds.maxX - cell.bounds.minX) * (cell.bounds.maxY - cell.bounds.minY);
		return sum + area;
	}, 0);
	const emissionAreaM2 = siteAreaM2 > 0 ? siteAreaM2 : gridAreaM2;
	const effectiveEmissionAreaM2 = Math.max(
		PHOTON_PHYSICAL_CONFIG.minimumProjectedAreaM2,
		emissionAreaM2
	);
	const directFluxW = effectiveEmissionAreaM2 * solarIrradiance.directHorizontalIrradianceWm2;
	const diffuseFluxW = effectiveEmissionAreaM2 * solarIrradiance.diffuseHorizontalIrradianceWm2;
	const globalFluxW = Math.max(1e-9, directFluxW + diffuseFluxW);
	const directPhotonFraction = directFluxW > 0 ? directFluxW / globalFluxW : 0;
	const diffusePhotonFraction = diffuseFluxW > 0 ? diffuseFluxW / globalFluxW : 0;
	const directPhotonsPerCell =
		directPhotonFraction > 0
			? Math.max(1, Math.round(photonsPerCell * directPhotonFraction))
			: 0;
	const diffusePhotonsPerCell =
		diffusePhotonFraction > 0
			? Math.max(1, photonsPerCell - directPhotonsPerCell)
			: 0;
	const activePasses = [directPhotonsPerCell > 0 && directFluxW > 0, diffusePhotonsPerCell > 0 && diffuseFluxW > 0].filter(Boolean).length;
	if (activePasses === 0) {
		console.warn('No active direct or diffuse photon passes could be created.');
		return;
	}

	const runPhotonWorkerPass = async (
		emissionMode: 'direct' | 'diffuse',
		passPhotonsPerCell: number,
		passFluxW: number,
		progressStart: number,
		progressSpan: number
	): Promise<void> => {
		if (passPhotonsPerCell <= 0 || passFluxW <= 0) return;

		const totalPhotonPackets = Math.max(1, lastPhotonGridCells.length * passPhotonsPerCell);
		const photonPowerWatts = passFluxW / totalPhotonPackets;
		await executePhotonWorkerPass({
			cells: lastPhotonGridCells,
			shadowCasters,
			sunPosition,
			sceneCenter,
			sceneDiskRadius,
			photonsPerCell: passPhotonsPerCell,
			photonPowerWatts,
			emissionMode,
			maxBounces: PHOTON_MAP_CONFIG.maxBounces,
			siteLimitPolygon: siteLimitPoints.map((p) => ({ x: p.x, y: p.y })),
			collectVisualizationData: false,
			collectPhotonPaths:
				emissionMode === 'direct' &&
				PHOTON_MAP_CONFIG.visualizationConfig.showPaths &&
				PHOTON_MAP_CONFIG.visualizationConfig.interactivePaths,
			maxPhotonPaths: PHOTON_MAP_CONFIG.visualizationConfig.maxInteractivePaths,
			onProgress: (passProgress) => {
				if (!progressCallback) return;
				const combinedProgress = Math.min(1, progressStart + passProgress * progressSpan);
				progressCallback(combinedProgress, {
					current: Math.round(combinedProgress * lastPhotonGridCells.length),
					total: lastPhotonGridCells.length,
				});
			},
			onComplete: (data) => {
				const bounceOffset = emissionMode === 'diffuse' ? 1 : 0;
				const gPos = data.globalPositions as Float32Array;
				const gNrm = data.globalNormals as Float32Array;
				const gPow = data.globalPowers as Float32Array;
				const gBnc = data.globalBounces as Uint16Array;
				globalPhotonMap.appendBatch(
					gPos,
					gNrm,
					gPow,
					gBnc,
					newPhotonScale,
					bounceOffset,
					photonVisualizationSystem
				);

				const cPos = data.causticPositions as Float32Array;
				const cNrm = data.causticNormals as Float32Array;
				const cPow = data.causticPowers as Float32Array;
				const cBnc = data.causticBounces as Uint16Array;
				causticPhotonMap.appendBatch(
					cPos,
					cNrm,
					cPow,
					cBnc,
					newPhotonScale,
					bounceOffset,
					photonVisualizationSystem
				);

				// Keep path visualization aligned with sun-origin expectation.
				// Diffuse sky photons are still accumulated physically, but their paths are not shown.
				if (emissionMode === 'direct' && interactivePhotonPathVisualization && data.photonPaths) {
					data.photonPaths.forEach((pathData: PhotonPath) => {
						const positions = pathData.positions.map(
							(p) => new THREE.Vector3(p.x, p.y, p.z)
						);
						interactivePhotonPathVisualization!.addPath(
							positions,
							pathData.bounceColors
						);
					});
				}
			},
		});
	};

	let completedPasses = 0;
	const passSpan = 1 / activePasses;
	if (directPhotonsPerCell > 0 && directFluxW > 0) {
		await runPhotonWorkerPass('direct', directPhotonsPerCell, directFluxW, completedPasses * passSpan, passSpan);
		completedPasses++;
	}
	if (diffusePhotonsPerCell > 0 && diffuseFluxW > 0) {
		await runPhotonWorkerPass('diffuse', diffusePhotonsPerCell, diffuseFluxW, completedPasses * passSpan, passSpan);
	}

	if (progressCallback) {
		progressCallback(1.0, {
			current: lastPhotonGridCells.length,
			total: lastPhotonGridCells.length,
		});
	}

	if (photonVisualizationSystem) {
		photonVisualizationSystem.finalize();
	}

	if (GRID_PHOTON_CONFIG.visualizeGrid && gridVisualization) {
		setTimeout(() => {
			gridVisualization?.clear(scene);
		}, 100);
	}

	photonMapValid = true;
	photonAccumulationRuns = nextRuns;
};
